import { Link } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';

function About() {
    const features = [
        {
            title: 'Complete Cost Breakdown',
            description: 'See the full picture with principal, interest, property tax, insurance, and PMI calculations - not just P&I like basic calculators.',
            icon: '💰'
        },
        {
            title: 'Advanced Amortization',
            description: 'View monthly or yearly breakdowns with visual charts showing exactly where your money goes each payment period.',
            icon: '📊'
        },
        {
            title: 'Prepayment Analysis',
            description: 'Discover how even small extra payments can save tens of thousands in interest and shave years off your mortgage.',
            icon: '🚀'
        },
        {
            title: 'Multi-Loan Comparison',
            description: 'Compare unlimited loan scenarios side-by-side to find the perfect option for your situation.',
            icon: '⚖️'
        },
        {
            title: 'Interest Rate Comparison',
            description: 'See how different rates affect your total cost with our ±1% comparison tool - unique to Principal Saver.',
            icon: '📈'
        },
        {
            title: 'Export & Share',
            description: 'Download professional PDF reports, Excel spreadsheets, or share your calculations via a unique URL.',
            icon: '📤'
        }
    ];

    const comparison = [
        { feature: 'Complete PITI Breakdown', ps: true, zillow: true, bankrate: true, nerdwallet: true, others: false },
        { feature: 'Detailed Amortization', ps: 'Monthly/Yearly', zillow: true, bankrate: true, nerdwallet: true, others: 'Limited' },
        { feature: 'Prepayment Analysis', ps: 'Advanced', zillow: false, bankrate: 'Limited', nerdwallet: 'Limited', others: false },
        { feature: 'Multi-Loan Comparison', ps: 'Unlimited', zillow: 'Limited', bankrate: 'Limited', nerdwallet: false, others: false },
        { feature: 'Interest Rate Comparison', ps: true, zillow: false, bankrate: false, nerdwallet: false, others: false },
        { feature: 'ARM Loan Support', ps: 'Full', zillow: 'Partial', bankrate: 'Partial', nerdwallet: false, others: false },
        { feature: 'Buydown Options', ps: true, zillow: false, bankrate: false, nerdwallet: false, others: false },
        { feature: 'PDF/Excel Export', ps: 'Both', zillow: 'Limited', bankrate: false, nerdwallet: false, others: false },
        { feature: 'Shareable Links', ps: true, zillow: false, bankrate: false, nerdwallet: false, others: false },
        { feature: 'No Ads', ps: true, zillow: false, bankrate: false, nerdwallet: false, others: false },
        { feature: '100% Free', ps: true, zillow: true, bankrate: true, nerdwallet: true, others: true }
    ];

    const renderCheckmark = (value, isHighlighted = false) => {
        // Use darker colors on blue background for better visibility
        const checkColor = isHighlighted ? '#059669' : '#10b981'; // Darker green on blue bg
        const xColor = isHighlighted ? '#dc2626' : '#ef4444'; // Darker red on blue bg

        if (value === true) return <CheckCircle size={20} style={{ color: checkColor }} />;
        if (value === false) return <XCircle size={20} style={{ color: xColor }} />;
        return <span style={{ fontSize: '0.875rem', color: isHighlighted ? '#1e293b' : 'var(--text-secondary)', fontWeight: isHighlighted ? '600' : '500' }}>{value}</span>;
    };

    return (
        <div className="container fade-in" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
            <header style={{ marginBottom: '3rem', textAlign: 'center' }}>
                <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>About Principal Saver</h1>
                <p style={{ fontSize: '1.25rem', color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
                    The most comprehensive free mortgage calculator, designed to help you make informed home financing decisions
                </p>
            </header>

            {/* Key Features */}
            <section style={{ marginBottom: '4rem' }}>
                <h2 style={{ fontSize: '2rem', marginBottom: '2rem', textAlign: 'center' }}>Key Features</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
                    {features.map((feature, index) => (
                        <div key={index} className="card" style={{ border: '1px solid var(--border)' }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{feature.icon}</div>
                            <h3 style={{ marginBottom: '0.75rem' }}>{feature.title}</h3>
                            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Comparison Table */}
            <section style={{ marginBottom: '4rem' }}>
                <h2 style={{ fontSize: '2rem', marginBottom: '2rem', textAlign: 'center' }}>How We Compare</h2>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--primary-light)', borderBottom: '2px solid var(--border)' }}>
                                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#1e293b' }}>Feature</th>
                                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#4f46e5' }}>Principal Saver</th>
                                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>Zillow</th>
                                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>Bankrate</th>
                                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>NerdWallet</th>
                                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>Others</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comparison.map((row, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ padding: '1rem', fontWeight: '500' }}>{row.feature}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center', backgroundColor: 'var(--primary-light)' }}>
                                        {renderCheckmark(row.ps, true)}
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{renderCheckmark(row.zillow)}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{renderCheckmark(row.bankrate)}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{renderCheckmark(row.nerdwallet)}</td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{renderCheckmark(row.others)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Why Principal Saver */}
            <section style={{ marginBottom: '4rem' }}>
                <h2 style={{ fontSize: '2rem', marginBottom: '2rem', textAlign: 'center' }}>Why Choose Principal Saver?</h2>
                <div className="card" style={{ border: '1px solid var(--border)' }}>
                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                        <div>
                            <h3 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>🎯 Complete Transparency</h3>
                            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                                Unlike basic calculators that only show principal and interest, we provide the complete picture including taxes, insurance, and PMI. No hidden costs, no surprises.
                            </p>
                        </div>
                        <div>
                            <h3 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>📊 Advanced Analysis</h3>
                            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                                Our prepayment calculator and multi-scenario comparison tools give you insights that help save thousands of dollars over the life of your loan.
                            </p>
                        </div>
                        <div>
                            <h3 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>🚫 Ad-Free Experience</h3>
                            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                                No ads, no lender solicitations, no distractions. Just a clean, professional tool focused on helping you make the best decision.
                            </p>
                        </div>
                        <div>
                            <h3 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>💯 Always Free</h3>
                            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                                No signup required, no credit card needed, no hidden fees. All features are completely free, forever.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <div className="card" style={{ backgroundColor: 'var(--primary-light)', border: 'none', textAlign: 'center' }}>
                <h3 style={{ marginTop: 0, color: '#1e293b' }}>Ready to Get Started?</h3>
                <p style={{ marginBottom: '1.5rem', color: '#475569' }}>
                    Start making smarter home financing decisions in 30 seconds
                </p>
                <Link
                    to="/"
                    className="btn btn-primary"
                    style={{ display: 'inline-block', textDecoration: 'none' }}
                >
                    Try the Calculator
                </Link>
            </div>
        </div>
    );
}

export default About;
